import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { FlaskConical, ArrowDown } from "lucide-react";
import { scrollToId } from "../lib/api";

const headline = ["Good doctors lose", "patients to average", "doctors because of", "social media."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

export default function Hero({ start = true }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yFlask = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const yPill = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen items-start overflow-hidden bg-white pt-32 pb-20 lg:items-center lg:pt-28 lg:pb-0">
      <div className="absolute inset-0 grid-pattern opacity-60" />

      {/* Floating lab elements */}
      <motion.div style={{ y: yFlask }} className="absolute right-[8%] top-[18%] hidden lg:block">
        <div className="animate-float-slow rounded-[2rem] border border-slate-200 bg-blue-light/60 p-6 backdrop-blur-sm">
          <FlaskConical className="h-16 w-16 text-blue-deep" strokeWidth={1.25} />
        </div>
      </motion.div>
      <motion.div style={{ y: yPill }} className="absolute left-[6%] bottom-[16%] hidden lg:block">
        <div className="flex h-14 w-32 -rotate-12 animate-float-slow overflow-hidden rounded-full shadow-lg" style={{ animationDelay: "1.5s" }}>
          <div className="h-full w-1/2 bg-blue-brand" />
          <div className="h-full w-1/2 bg-lab-green" />
        </div>
      </motion.div>
      <div className="absolute right-[22%] bottom-[24%] hidden h-6 w-6 animate-float-slow rounded-full bg-lab-green/70 lg:block" style={{ animationDelay: "0.8s" }} />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: start ? 1 : 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-2 w-2 rounded-full bg-lab-green" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
            Content Lab — Healthcare Marketing, Prescribed
          </span>
        </motion.div>

        <h1 className="font-display text-[2.15rem] font-extrabold leading-[1.05] tracking-tight text-obsidian sm:text-6xl sm:leading-[0.95] lg:text-7xl">
          {headline.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate={start ? "visible" : "hidden"} className="block">
                {line.includes("average") ? (
                  <>patients to <span className="text-blue-brand">average</span></>
                ) : line.includes("social media") ? (
                  <span className="text-lab-green">social media.</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-10 flex max-w-xl flex-col gap-6"
        >
          <p className="text-lg leading-relaxed text-slate-600">
            We turn brilliant clinicians into the obvious choice online. Content, social,
            websites &amp; Google presence — engineered in the lab, dosed for growth.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-audit"
              onClick={() => scrollToId("#services")}
              className="group rounded-full bg-blue-brand px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white shadow-[0_10px_30px_rgba(14,165,233,0.35)] transition-transform duration-300 hover:-translate-y-1"
            >
              See the lab
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-slate-400" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">Scroll</span>
      </motion.div>
    </section>
  );
}
